import React, { ReactElement, useState } from 'react';
import Modal from '../Modal';
import {
	CallLogData,
	ParticipantNameCallFieldMappingConfig, ParticipantNameMappingFormat
} from '../../store/state/createJobState';
import RadioControl from '../RadioControl';
import './ParticipantNameMappingConfigurationModal.scss';
import PreviewTable, { PreviewTableData } from '../PreviewTable';
import { getCallFieldMappingResult } from '../../store/selectors/createJobSelectors';

enum ColumnSeparateMode {
	Unset = 'Unset',
	SameColumn = 'SameColumn',
	SeparateColumns = 'SeparateColumns'
}

const noSelectionValue = '____empty____';

export default function ParticipantNameMappingConfigurationModal(props: Props) {
	const [mode, setMode] = useState<ColumnSeparateMode>(getInitialColumnSeparateMode(props.config));
	const [sameColumnFormat, setSameColumnFormat] = useState<ParticipantNameMappingFormat | null>(props.config.modeConfig.type == 'singleField' ? props.config.modeConfig.format : null);
	const [sameColumnIndex, setSameColumnIndex] = useState<number | null>(props.config.modeConfig.type === 'singleField' ? props.config.modeConfig.columnIndex : null);
	const [firstColumnIndex, setFirstColumnIndex] = useState<number | null>(props.config.modeConfig.type === 'multiField' ? props.config.modeConfig.firstNameColumnIndex : null);
	const [lastColumnIndex, setLastColumnIndex] = useState<number | null>(props.config.modeConfig.type === 'multiField' ? props.config.modeConfig.lastNameColumnIndex : null);
	const previewData = getPreviewData();
	
	return (
		<React.Fragment>
			<Modal onClose={props.onCancel} renderActions={renderActions}>
				<React.Fragment>
					<h3 className="h3-mb-3">Configure <span className="text-bold">{props.config.displayName}</span> parameters</h3>

					<div className="mb-3">How is the {props.config.displayName} formatted in your call log?</div>
					
					<div>
						<div className="mb-2">
							<RadioControl label="First and last name are in same column" selected={mode === ColumnSeparateMode.SameColumn} onSelected={() => setMode(ColumnSeparateMode.SameColumn)} />
						</div>
						<div className="mb-2">
							<RadioControl label="First and last name are in separate columns" selected={mode === ColumnSeparateMode.SeparateColumns} onSelected={() => setMode(ColumnSeparateMode.SeparateColumns)} />
						</div>
					</div>
					
					{mode === ColumnSeparateMode.SameColumn && 
						<div className="mt-4">
							<div className="mb-1">Which column contains the {props.config.displayName}?</div>
							{renderColumnSelector(sameColumnIndex, setSameColumnIndex)}
							
							{sameColumnIndex != null &&
								<div className="mt-4">
									<div className="mb-1">How is the {props.config.displayName} formatted in this column?</div>
									<div>
										<select className="participant-name-mapping-configuration-modal__select" 
												value={sameColumnFormat ?? noSelectionValue} 
												onChange={e => e.target.value === noSelectionValue ? setSameColumnFormat(null) : setSameColumnFormat(e.target.value as ParticipantNameMappingFormat)}>
											<option value={noSelectionValue}>No selection</option>
											<option value={ParticipantNameMappingFormat.FirstSpaceLast}>First Last</option>
											<option value={ParticipantNameMappingFormat.LastCommaFirst}>Last, First</option>
										</select>
									</div>
								</div>
							}
						</div>
					}

					{mode === ColumnSeparateMode.SeparateColumns &&
						<div className="mt-4">
							<div className="mb-1">Which column contains the first name?</div>
							{renderColumnSelector(firstColumnIndex, setFirstColumnIndex, lastColumnIndex)}
	
							<div className="mt-4">
								<div className="mb-1">Which column contains the last name?</div>
								{renderColumnSelector(lastColumnIndex, setLastColumnIndex, firstColumnIndex)}
							</div>
						</div>
					}
					
					{!!previewData &&
						<div className="mt-4">
							<PreviewTable tableData={previewData} />
						</div>
					}
					
				</React.Fragment>
			</Modal>
		</React.Fragment>
	);
	
	function renderColumnSelector(selectedIndex: number | null, setSelectedIndex: (val: number | null) => void, indexToDisable?: number | nundefined): ReactElement {
		return (
			<div>
				<select className="participant-name-mapping-configuration-modal__select" value={selectedIndex ?? noSelectionValue}  onChange={e => setIndexValue(e.target.value, setSelectedIndex)}>
					<option value={noSelectionValue}>No selection</option>
					{props.callLogData.headers.map((header, i) => (
						<option key={i} value={i.toString()} disabled={props.allocatedColumnIndexes.has(i) || (indexToDisable != null && indexToDisable === i)}>{header}</option>
					))}
				</select>
			</div>
		)
	}

	function renderActions(): ReactElement {
		return (
			<div className="text-right">
				<button className="btn btn_link mr-2" onClick={props.onCancel}>Cancel</button>
				<button className="btn" onClick={onDone} disabled={isDoneDisabled()}>Done</button>
			</div>
		)
	}
	
	function setIndexValue(index: string, setter: (val: number | null) => void): void {
		if (index === noSelectionValue) {
			setter(null);
		} else {
			const parsed = parseInt(index);
			if (!isNaN(parsed)) {
				setter(parsed);
			}
		}
	}

	function getInitialColumnSeparateMode(config: ParticipantNameCallFieldMappingConfig): ColumnSeparateMode {
		if (config.modeConfig.type == 'singleField') {
			return ColumnSeparateMode.SameColumn;
		} else {
			return config.modeConfig.firstNameColumnIndex != null || config.modeConfig.lastNameColumnIndex != null 
				? ColumnSeparateMode.SeparateColumns 
				: ColumnSeparateMode.SameColumn;
		}
	}
	
	function getPreviewData(): PreviewTableData | null {
		const config = getCurrentConfig();

		const indexes: number[] = [];
		
		if (!config) {
			if (mode === ColumnSeparateMode.SameColumn && sameColumnIndex != null) {
				indexes.push(sameColumnIndex);
			}
		} else if (config.modeConfig.type === 'singleField') {
			indexes.push(config.modeConfig.columnIndex);
		} else {
			if (config.modeConfig.firstNameColumnIndex != null) {
				indexes.push(config.modeConfig.firstNameColumnIndex)
			}
			
			if (config.modeConfig.lastNameColumnIndex != null) {
				indexes.push(config.modeConfig.lastNameColumnIndex);
			}
		}
		
		if (!indexes.length) return null;

		const maxRowsToRender = 100;

		return {
			header: getIndexValuesSummary(props.callLogData.headers, indexes),
			rows: props.callLogData.callRows.slice(0, maxRowsToRender).map(row => {
				return {
					rawValue: getIndexValuesSummary(row, indexes),
					mappingResult: !config ? { type: 'single', value: { type: 'none' }} : getCallFieldMappingResult(row, config)
				};
			}),
			rowsAreTruncated: props.callLogData.callRows.length > maxRowsToRender
		}
	}
	
	function getIndexValuesSummary(values: string[], indexes: number[]): string {
		const result: string[] = [];
		
		indexes.forEach(i => {
			result.push(values[i] ?? 'Unknown');
		});
		
		return result.join(', ');
	}

	function getCurrentConfig(): ParticipantNameCallFieldMappingConfig | null {
		if (mode === ColumnSeparateMode.SameColumn) {
			if (sameColumnIndex != null && sameColumnFormat != null) {
				return {
					...props.config,
					modeConfig: { type: 'singleField', columnIndex: sameColumnIndex, format: sameColumnFormat }
				}
			}
		} else if (mode === ColumnSeparateMode.SeparateColumns) {
			if (firstColumnIndex != null || lastColumnIndex != null) {
				return {
					...props.config,
					modeConfig: { type: 'multiField', firstNameColumnIndex: firstColumnIndex, lastNameColumnIndex: lastColumnIndex }
				}
			}
		}
		
		return null;
	}

	function isDoneDisabled(): boolean {
		return !getCurrentConfig();
	}

	function onDone(): void {
		if (isDoneDisabled()) return;

		const config = getCurrentConfig();
		if (config) {
			props.onConfigured(config);
		}
	}
}

interface Props {
	config: ParticipantNameCallFieldMappingConfig;
	callLogData: CallLogData;
	allocatedColumnIndexes: Set<number>;
	onCancel: () => void;
	onConfigured: (config: ParticipantNameCallFieldMappingConfig) => void;
}
